<script setup>
/**
 * O aviso que sobe no canto quando chega mensagem do chat interno.
 *
 * Pedido do Victor em 2026-08-06, com a referência explícita: "você já viu como
 * funcionava o MSN quando alguém mandava uma mensagem? Subia mostrando o nome
 * da pessoa e a mensagem". Embaixo, à direita — ele considerou em cima e
 * concluiu embaixo, que é onde o MSN punha.
 *
 * MORA NO APP INTEIRO, não na tela do chat: o ponto é justamente avisar quem
 * está no Pipeline, na Agenda ou no Financeiro. Quem já está na conversa não vê
 * aviso nenhum — a mensagem apareceu ali, e um aviso em cima disso é sinal
 * duplicado.
 *
 * NÃO cobre "a pessoa está em outro site". Aba em segundo plano é outro
 * mecanismo — a notificação do sistema operacional, que já existe e passa pelo
 * service worker. Este componente só desenha dentro do advup.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { emitter } from 'shared/helpers/mitt';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  stripMarkdownLite,
  stripMentionMarkup,
  stripReferencias,
} from './useMessageContent';
import { AVISO_DE_MENSAGEM } from './eventos';

const TEMPO_NA_TELA = 6000;
// Mais que isso vira parede de tarja. O MSN empilhava no máximo umas poucas.
const MAXIMO_NA_TELA = 3;

const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const avisos = ref([]);
const temporizadores = new Map();

const remover = id => {
  avisos.value = avisos.value.filter(a => a.id !== id);
  const t2 = temporizadores.get(id);
  if (t2) {
    clearTimeout(t2);
    temporizadores.delete(id);
  }
};

/**
 * O texto que aparece na tarja. Passa pelas MESMAS funções que a prévia da
 * barra lateral usa — sem isso o aviso mostraria `**negrito**` e
 * `[jane](advup://cliente/5)` crus, que foi o problema que o editor rico
 * acabou de resolver na bolha.
 */
const textoLimpo = aviso => {
  const cru = stripReferencias(stripMentionMarkup(aviso.previa || ''));
  const limpo = stripMarkdownLite(cru).trim();
  if (limpo) return limpo;
  return aviso.temAnexo
    ? t('INTERNAL_CHATS.ATTACHMENT_LABEL')
    : t('INTERNAL_CHATS.AVISO.SEM_TEXTO');
};

const abrir = aviso => {
  remover(aviso.id);
  router.push(
    accountScopedRoute('internal_chat_detail', { chatId: aviso.chatId })
  );
};

const aoChegar = dados => {
  if (!dados?.chatId) return;

  const aviso = {
    // O id do chat entra na chave: duas mensagens seguidas da MESMA conversa
    // devem virar duas tarjas distintas, e não uma sobrescrevendo a outra.
    id: `${dados.chatId}-${dados.recebidoEm}`,
    ...dados,
  };

  avisos.value = [aviso, ...avisos.value].slice(0, MAXIMO_NA_TELA);
  temporizadores.set(
    aviso.id,
    setTimeout(() => remover(aviso.id), TEMPO_NA_TELA)
  );
};

onMounted(() => emitter.on(AVISO_DE_MENSAGEM, aoChegar));
onBeforeUnmount(() => {
  emitter.off(AVISO_DE_MENSAGEM, aoChegar);
  temporizadores.forEach(clearTimeout);
  temporizadores.clear();
});
</script>

<template>
  <!-- `pointer-events-none` no container e `auto` na tarja: a área vazia ao
       redor não pode roubar clique de quem está trabalhando atrás. -->
  <div
    class="pointer-events-none fixed bottom-4 z-[9999] flex flex-col-reverse gap-2 ltr:right-4 rtl:left-4"
  >
    <TransitionGroup name="aviso">
      <div
        v-for="aviso in avisos"
        :key="aviso.id"
        class="pointer-events-auto flex w-80 max-w-[calc(100vw-2rem)] cursor-pointer items-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3 shadow-xl transition-colors hover:bg-n-alpha-1"
        role="button"
        tabindex="0"
        @click="abrir(aviso)"
        @keydown.enter="abrir(aviso)"
      >
        <Avatar :name="aviso.remetente" :size="32" rounded-full />

        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-1">
            <span class="truncate text-sm font-semibold text-n-slate-12">
              {{ aviso.remetente }}
            </span>
            <!-- Onde a mensagem caiu. Numa DM o nome do chat é a própria
                 pessoa, então repetiria o remetente — por isso só aparece
                 quando é diferente. -->
            <span
              v-if="aviso.nomeDoChat && aviso.nomeDoChat !== aviso.remetente"
              class="truncate text-xs text-n-slate-10"
            >
              {{ aviso.nomeDoChat }}
            </span>
          </div>
          <p class="mt-0.5 line-clamp-2 text-sm text-n-slate-11">
            {{ textoLimpo(aviso) }}
          </p>
        </div>

        <button
          v-tooltip.left="t('INTERNAL_CHATS.AVISO.FECHAR')"
          type="button"
          class="grid size-6 shrink-0 place-items-center rounded p-0 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-label="t('INTERNAL_CHATS.AVISO.FECHAR')"
          @click.stop="remover(aviso.id)"
        >
          <Icon icon="i-lucide-x" class="size-3.5 shrink-0" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
/* Sobe de baixo, como o do MSN. */
.aviso-enter-active,
.aviso-leave-active {
  transition:
    translate var(--mov-3) var(--mov-curva),
    opacity var(--mov-3) var(--mov-curva);
}

.aviso-enter-from,
.aviso-leave-to {
  translate: 0 var(--esp-3);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .aviso-enter-active,
  .aviso-leave-active {
    transition: none;
  }
}
</style>
